import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Tenant } from '../../features/tenant/services/tenant.service';
import { LOCAL_STORAGE_KEYS } from '../constants/local-storage-keys';
import { StorageService } from './storage.service';

/**
 * Service for managing the currently selected tenant.
 * Provides reactive state management and persistence across page reloads.
 *
 * @remarks
 * This service is central to the application's multi-tenant functionality:
 * - Manages tenant selection state
 * - Persists selection to localStorage
 * - Provides reactive Observable for components to subscribe
 * - Automatically syncs with storage changes (cross-tab synchronization)
 *
 * @example
 * ```typescript
 * // Subscribe to tenant changes
 * tenantSelectionService.selectedTenant$.subscribe(tenant => {
 *   if (tenant) {
 *     console.log('Selected tenant:', tenant.name);
 *   }
 * });
 *
 * // Set selected tenant
 * tenantSelectionService.setSelectedTenant(tenant);
 *
 * // Get current tenant
 * const currentTenant = tenantSelectionService.getSelectedTenant();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class TenantSelectionService {
  private storageService = inject(StorageService);

  private selectedTenantSubject = new BehaviorSubject<Tenant | null>(null);

  /**
   * Observable that emits whenever the selected tenant changes.
   * Components should subscribe to this for reactive updates.
   */
  public selectedTenant$: Observable<Tenant | null> = this.selectedTenantSubject.asObservable();

  constructor() {
    this.loadFromStorage();

    // Listen for storage changes via StorageService
    this.storageService.storageChange$.subscribe((change) => {
      if (change && change.key === LOCAL_STORAGE_KEYS.TENANT) {
        this.loadFromStorage();
      }
    });
  }

  /**
   * Internal method to load tenant from storage and update state.
   * Only updates if the tenant ID has changed to prevent unnecessary emissions.
   */
  private loadFromStorage(): void {
    const tenant = this.storageService.getItem<Tenant>(LOCAL_STORAGE_KEYS.TENANT);
    const currentTenant = this.selectedTenantSubject.value;

    if (tenant) {
      if (!currentTenant || currentTenant.id !== tenant.id) {
        this.selectedTenantSubject.next(tenant);
      }
    } else {
      this.selectedTenantSubject.next(null);
    }
  }

  /**
   * Gets the currently selected tenant synchronously.
   *
   * @returns The selected tenant object, or null if no tenant is selected
   */
  getSelectedTenant(): Tenant | null {
    return this.selectedTenantSubject.value;
  }

  /**
   * Sets the selected tenant and persists it to storage.
   * Updates both the BehaviorSubject state and localStorage.
   *
   * @param tenant - The tenant to select, or null to clear selection
   */
  setSelectedTenant(tenant: Tenant | null): void {
    this.selectedTenantSubject.next(tenant);
    if (tenant) {
      this.storageService.setItem(LOCAL_STORAGE_KEYS.TENANT, tenant);
    } else {
      this.storageService.removeItem(LOCAL_STORAGE_KEYS.TENANT);
    }
  }
}
